/**
 * Supplies tab — pure cart helpers. The cart is a quantity per supply item;
 * everything the summary column shows (lines, subtotal, the order-level
 * "Ships in N days") derives from it against the catalog. Mirrors the POS
 * register's `pos-sale-sheet-helpers` shapes so the two surfaces read alike.
 */
import { supplyShipWindowLabel, widestSupplyShipWindow } from '@ordo/constants/supply-taxonomy'

import type { SupplyCatalogItemWire, SupplyCatalogLineWire } from '@/api/supplies'

export type BulkCatalogScope = 'all' | 'backup' | 'supplies'
export function bulkCatalogForScope(catalog: ReadonlyArray<SupplyCatalogLineWire>, scope: BulkCatalogScope) {
  return catalog.filter(
    (line) =>
      scope === 'all' ||
      (scope === 'backup' ? line.lineCode === 'SHELF_STABLE_MEALS' : line.lineCode !== 'SHELF_STABLE_MEALS'),
  )
}

export type SupplyCart = Readonly<Record<string, number>>

export const EMPTY_SUPPLY_CART: SupplyCart = {}

export interface SupplyCartLine {
  item: SupplyCatalogItemWire
  quantity: number
  totalCents: number
}

/** Sane per-line ceiling — matches the server's MAX_SUPPLY_LINE_QUANTITY. */
export const MAX_SUPPLY_LINE_QUANTITY = 999

export function supplyCartAdd(cart: SupplyCart, supplyItemId: string): SupplyCart {
  const current = cart[supplyItemId] ?? 0
  if (current >= MAX_SUPPLY_LINE_QUANTITY) {
    return cart
  }
  return { ...cart, [supplyItemId]: current + 1 }
}

export function supplyCartDecrement(cart: SupplyCart, supplyItemId: string): SupplyCart {
  const current = cart[supplyItemId] ?? 0
  if (current <= 0) {
    return cart
  }
  if (current === 1) {
    const { [supplyItemId]: _removed, ...rest } = cart
    return rest
  }
  return { ...cart, [supplyItemId]: current - 1 }
}

export function supplyCartCount(cart: SupplyCart): number {
  return Object.values(cart).reduce((sum, q) => sum + q, 0)
}

/** Cart → lines in CATALOG order (line, then item), dropping items no longer in the catalog. */
export function supplyCartLines(cart: SupplyCart, catalog: ReadonlyArray<SupplyCatalogLineWire>): SupplyCartLine[] {
  const lines: SupplyCartLine[] = []
  for (const line of catalog) {
    for (const item of line.items) {
      const quantity = cart[item.supplyItemId] ?? 0
      if (quantity > 0) {
        lines.push({ item, quantity, totalCents: item.customerPriceCents * quantity })
      }
    }
  }
  return lines
}

export function supplyCartSubtotalCents(lines: ReadonlyArray<SupplyCartLine>): number {
  return lines.reduce((sum, l) => sum + l.totalCents, 0)
}

/** The order-level promise: the widest window across the cart's items. */
export function supplyCartShipLabel(lines: ReadonlyArray<SupplyCartLine>): string | null {
  if (lines.length === 0) {
    return null
  }
  if (lines.some((line) => line.item.delivery != null)) {
    return [...new Set(lines.map((line) => line.item.shipLabel))].join(' · ')
  }
  return supplyShipWindowLabel(
    widestSupplyShipWindow(lines.map((l) => ({ minDays: l.item.shipMinDays, maxDays: l.item.shipMaxDays }))),
  )
}

/** The POST body; zero rows are already absent from the cart. */
export function supplyCartToLines(cart: SupplyCart): Array<{ supplyItemId: string; quantity: number }> {
  return Object.entries(cart)
    .filter(([, quantity]) => quantity > 0)
    .map(([supplyItemId, quantity]) => ({ supplyItemId, quantity }))
}

/** Only submit visible cart lines and the unit prices the user just reviewed. */
export function supplyCartReviewedLines(lines: ReadonlyArray<SupplyCartLine>) {
  return lines.map(({ item, quantity }) => ({
    supplyItemId: item.supplyItemId,
    quantity,
    expectedUnitPriceCents: item.customerPriceCents,
  }))
}

/**
 * Inline-alert copy for the stable error codes `POST /api/supplies/orders`
 * returns. Unknown codes fall back to the server message.
 */
export function supplyOrderErrorCopy(code: string | null, fallback: string): { title: string; body: string } {
  switch (code) {
    case 'insufficient_funds':
      return {
        title: 'Not enough balance',
        body: 'This order is more than the school account holds. Add funds under Billing, or reduce the order.',
      }
    case 'credit_limit_exceeded':
      return {
        title: 'Over the credit limit',
        body: 'This order would take the school account past its credit limit. Contact Ordo to raise it, or reduce the order.',
      }
    case 'billing_mode_unset':
    case 'no_billing_account':
      return {
        title: 'Billing not set up',
        body: 'The school account is not ready to be charged yet. Ordo needs to finish billing provisioning first.',
      }
    case 'supply_prices_changed':
      return {
        title: 'Prices have changed',
        body: 'Review the updated total, then place the order again. No order was placed.',
      }
    case 'supply_not_available':
    case 'supply_item_unavailable':
      return {
        title: 'An item is no longer available',
        body: 'Something in the cart was paused or removed from the catalog. Remove it and try again.',
      }
    default:
      return { title: 'Could not place the order', body: fallback }
  }
}

export function formatSupplyCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}
